import React, { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  Typography,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Breadcrumbs,
  Link,
  Avatar,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { authStore } from '@/store/authStore'
import { useSnackbar } from 'notistack'
import { Cliente, ContatoRequest, VolumeMensal } from '@/types/cliente'
import { Documento } from '@/types/documento'
import { clienteService } from '@/services/clienteService'
import { documentoService } from '@/services/documentoService'
import { oportunidadeService } from '@/services/oportunidadeService'
import { TIPO_ENTIDADE_OPORTUNIDADE } from '@/utils/docsObrigatorios'
import { formatCNPJ, formatCurrency, formatDate, formatFileSize } from '@/utils/formatters'
import { validateEmail, validatePhone } from '@/utils/validators'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

/** Mensagem centralizada para abas sem dados / ainda não integradas. */
const EmptyState: React.FC<{ texto: string }> = ({ texto }) => (
  <Card>
    <CardContent sx={{ textAlign: 'center' }}>
      <Typography variant="body2" color="textSecondary">
        {texto}
      </Typography>
    </CardContent>
  </Card>
)

const novoContato: ContatoRequest = {
  nome: '',
  email: '',
  telefone: '',
  cargo: '',
  principal: false,
}

/**
 * Rótulo do segmento sem precisar buscar o catálogo aqui: o cadastro grava o rótulo
 * escolhido em `setorIndustria`, e "Outros" vale mais mostrado pela descrição livre
 * do que pela palavra "Outros". Vazio quando o cliente é anterior ao campo.
 */
const rotuloSegmento = (cliente: Cliente): string => {
  if (!cliente.segmento) return ''
  if (cliente.segmento === 'OUTROS') return cliente.segmentoDescricao || 'Outros'
  return cliente.setorIndustria
}

const MESES_ABREV = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
]

/**
 * "2026-06-01" -> "jun/2026".
 *
 * Montado a partir da string, sem `new Date`: `new Date('2026-06-01')` é meia-noite UTC
 * e, em fuso negativo, exibiria "mai/2026". E o `toLocaleDateString('pt-BR')` com mês
 * curto devolve "jun. de 2026", que não é o formato que a tabela quer.
 */
const rotuloCompetencia = (iso: string): string => {
  const [ano, mes] = iso.split('-')
  const indice = Number(mes) - 1
  return MESES_ABREV[indice] ? `${MESES_ABREV[indice]}/${ano}` : iso
}

const ClienteDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [cliente, setCliente] = useState<Cliente | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [tabValue, setTabValue] = useState(0)
  const [contatoDialogOpen, setContatoDialogOpen] = useState(false)

  // Volume operado (2º PDF, Etapa 18)
  const usuario = authStore((st) => st.usuario)
  const podeLancarVolume = !!usuario?.permissoes?.some((perm) =>
    ['OPERADOR', 'GERENTE', 'ADMIN'].includes(perm.toUpperCase())
  )
  const [volumes, setVolumes] = useState<VolumeMensal[]>([])
  const [volumeDialogOpen, setVolumeDialogOpen] = useState(false)
  const [salvandoVolume, setSalvandoVolume] = useState(false)
  const [novoVolume, setNovoVolume] = useState({
    competencia: new Date().toISOString().slice(0, 7),
    valor: '',
    observacao: '',
  })
  const [newContato, setNewContato] = useState<ContatoRequest>(novoContato)
  const [contatoErrors, setContatoErrors] = useState<Record<string, string>>({})
  const [salvandoContato, setSalvandoContato] = useState(false)

  // Documentos do cliente (carregados sob demanda ao abrir a aba).
  const [documentos, setDocumentos] = useState<Documento[]>([])
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsErro, setDocsErro] = useState<string | null>(null)
  const [docsCarregados, setDocsCarregados] = useState(false)

  const carregar = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setErro(null)
    try {
      setCliente(await clienteService.buscar(id))
    } catch (e) {
      setErro((e as { message?: string })?.message ?? 'Cliente não encontrado')
      setCliente(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    carregar()
  }, [carregar])

  const carregarVolumes = useCallback(async () => {
    if (!id) return
    try {
      setVolumes(await clienteService.historicoVolumes(id))
    } catch {
      // Histórico indisponível não derruba a página do cliente.
      setVolumes([])
    }
  }, [id])

  useEffect(() => {
    carregarVolumes()
  }, [carregarVolumes])

  const salvarVolume = async () => {
    if (!id) return
    const valor = Number(novoVolume.valor)
    if (!novoVolume.competencia || Number.isNaN(valor) || valor < 0) return
    setSalvandoVolume(true)
    try {
      // O backend normaliza para o dia 1; mandar `-01` só evita depender disso.
      await clienteService.registrarVolume(id, {
        competencia: `${novoVolume.competencia}-01`,
        volume: valor,
        observacao: novoVolume.observacao.trim() || undefined,
      })
      enqueueSnackbar('Volume registrado', { variant: 'success' })
      setVolumeDialogOpen(false)
      setNovoVolume({ competencia: new Date().toISOString().slice(0, 7), valor: '', observacao: '' })
      await carregarVolumes()
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Falha ao registrar volume',
        { variant: 'error' }
      )
    } finally {
      setSalvandoVolume(false)
    }
  }

  const removerVolume = async (volumeId: string) => {
    if (!id) return
    try {
      await clienteService.removerVolume(id, volumeId)
      await carregarVolumes()
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Falha ao remover lançamento',
        { variant: 'error' }
      )
    }
  }

  // Carrega os documentos só quando a aba Documentos (índice 2) é aberta.
  // Os documentos ficam vinculados às OPORTUNIDADES (entidadeReferencia=oportunidadeId),
  // não ao cliente. Então: busca as oportunidades do cliente e agrega os docs de cada uma
  // (mesmo padrão das telas de Reservas/Estruturação).
  useEffect(() => {
    if (tabValue !== 2 || !cliente || docsCarregados) return
    let ativo = true
    ;(async () => {
      setDocsLoading(true)
      setDocsErro(null)
      try {
        // O backend filtra oportunidades por clienteId via query param (?clienteId=).
        const opPage = await oportunidadeService.filtrar(0, 500, { clienteId: cliente.id })
        const oportunidades = opPage.content ?? []
        const porOportunidade = await Promise.all(
          oportunidades.map((o) =>
            documentoService
              .listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, o.id, 0, 500)
              .then((p) => p.content ?? [])
              .catch(() => [] as Documento[]),
          ),
        )
        if (ativo) {
          setDocumentos(porOportunidade.flat())
          setDocsCarregados(true)
        }
      } catch (e) {
        if (ativo) setDocsErro((e as { message?: string })?.message ?? 'Falha ao carregar documentos')
      } finally {
        if (ativo) setDocsLoading(false)
      }
    })()
    return () => {
      ativo = false
    }
  }, [tabValue, cliente, docsCarregados])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleAddContato = useCallback(async () => {
    if (!cliente) return
    const errors: Record<string, string> = {}
    if (!newContato.nome.trim()) errors.nome = 'Nome é obrigatório'
    if (!validateEmail(newContato.email)) errors.email = 'Email inválido'
    if (!validatePhone(newContato.telefone)) errors.telefone = 'Telefone inválido'
    if (!newContato.cargo.trim()) errors.cargo = 'Cargo é obrigatório'
    if (Object.keys(errors).length > 0) {
      setContatoErrors(errors)
      return
    }

    setSalvandoContato(true)
    try {
      const atualizado = await clienteService.adicionarContato(cliente.id, newContato)
      setCliente(atualizado)
      enqueueSnackbar('Contato adicionado', { variant: 'success' })
      setContatoDialogOpen(false)
      setNewContato(novoContato)
      setContatoErrors({})
    } catch (e) {
      enqueueSnackbar((e as { message?: string })?.message ?? 'Falha ao adicionar contato', {
        variant: 'error',
      })
    } finally {
      setSalvandoContato(false)
    }
  }, [cliente, newContato, enqueueSnackbar])

  const handleEditCliente = useCallback(() => {
    if (!cliente) return
    navigate(`/clientes/${cliente.id}/editar`, { state: { cliente } })
  }, [navigate, cliente])

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (erro || !cliente) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component="button" variant="body2" onClick={() => navigate('/clientes')} sx={{ cursor: 'pointer' }}>
            Clientes
          </Link>
          <Typography color="textPrimary">Detalhe</Typography>
        </Breadcrumbs>
        <Alert severity="error">{erro ?? 'Cliente não encontrado'}</Alert>
      </Box>
    )
  }

  const contatos = cliente.contatos ?? []

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/clientes')}
          sx={{ cursor: 'pointer' }}
        >
          Clientes
        </Link>
        <Typography color="textPrimary">{cliente.razaoSocial}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          {cliente.razaoSocial}
        </Typography>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditCliente}>
          Editar
        </Button>
      </Stack>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Informações" />
          <Tab label="Oportunidades" />
          <Tab label="Documentos" />
          <Tab label="Contatos" />
          <Tab label="Volume Operado" />
        </Tabs>
      </Paper>

      {/* Tab: Informações */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      CNPJ
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCNPJ(cliente.cnpj)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Tipo
                    </Typography>
                    <Chip
                      label={cliente.tipo === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                      color={cliente.tipo === 'PJ' ? 'primary' : 'secondary'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={cliente.status}
                      color={
                        cliente.status === 'ATIVO'
                          ? 'success'
                          : cliente.status === 'INATIVO'
                            ? 'error'
                            : 'info'
                      }
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Segmento
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {/* Cadastros anteriores ao campo só têm o texto livre. */}
                      {rotuloSegmento(cliente) || cliente.setorIndustria || '—'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Faturamento Anual
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(cliente.faturamentoAnual)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Data de Criação
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDate(cliente.createdAt)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Operacional — só existe para cliente que nasceu/foi ativado por uma reserva. */}
          {(cliente.produtoContratado || cliente.dataAtivacao || cliente.volumeContratado) && (
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Operação
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Produto contratado
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {cliente.produtoContratado || '—'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Volume contratado
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {cliente.volumeContratado != null
                          ? formatCurrency(cliente.volumeContratado)
                          : '—'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Em operação desde
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {cliente.dataAtivacao ? formatDate(cliente.dataAtivacao) : '—'}
                      </Typography>
                    </Box>

                    {cliente.reservaOrigemId && (
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Reserva de origem
                        </Typography>
                        <Typography variant="body2">
                          <Button
                            size="small"
                            sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                            onClick={() => navigate('/reservas')}
                          >
                            Ver reservas
                          </Button>
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Tab: Oportunidades (serviço próprio — integração pendente) */}
      <TabPanel value={tabValue} index={1}>
        <EmptyState texto="As oportunidades deste cliente serão exibidas aqui em breve." />
      </TabPanel>

      {/* Tab: Documentos */}
      <TabPanel value={tabValue} index={2}>
        {docsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : docsErro ? (
          <Alert severity="error">{docsErro}</Alert>
        ) : documentos.length === 0 ? (
          <EmptyState texto="Nenhum documento enviado para este cliente." />
        ) : (
          <Grid container spacing={2}>
            {documentos.map((doc) => (
              <Grid item xs={12} sm={6} md={4} key={doc.id}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {doc.nome}
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="textSecondary">
                          {doc.tipo}
                        </Typography>
                        <Chip
                          label={doc.status}
                          size="small"
                          color={
                            doc.status === 'PROCESSADO'
                              ? 'success'
                              : doc.status === 'ERRO'
                                ? 'error'
                                : 'warning'
                          }
                        />
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {formatFileSize(doc.tamanhoBytes)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(doc.createdAt)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Tab: Contatos */}
      <TabPanel value={tabValue} index={3}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setContatoDialogOpen(true)}
          sx={{ mb: 2 }}
        >
          Adicionar Contato
        </Button>

        <Grid container spacing={2}>
          {contatos.map((contato) => (
            <Grid item xs={12} sm={6} md={4} key={contato.id}>
              <Card>
                <CardHeader
                  avatar={<Avatar>{contato.nome.charAt(0)}</Avatar>}
                  title={contato.nome}
                  subheader={contato.cargo}
                />
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="body2">{contato.email}</Typography>
                    <Typography variant="body2">{contato.telefone}</Typography>
                    {contato.principal && (
                      <Chip label="Contato Principal" size="small" color="primary" />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {contatos.length === 0 && <EmptyState texto="Nenhum contato adicionado" />}
      </TabPanel>

      {/* Tab: Volume Operado — 2º PDF, Etapa 18 (Gestão dos Clientes Ativos) */}
      <TabPanel value={tabValue} index={4}>
        {podeLancarVolume && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setVolumeDialogOpen(true)}
            sx={{ mb: 2 }}
          >
            Registrar Volume do Mês
          </Button>
        )}

        {volumes.length === 0 ? (
          <EmptyState
            texto={
              podeLancarVolume
                ? 'Nenhum volume registrado. Lance o volume operado mês a mês para acompanhar a evolução.'
                : 'Nenhum volume registrado ainda pela Originei.'
            }
          />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Competência</TableCell>
                  <TableCell align="right">Volume</TableCell>
                  <TableCell align="right">Variação</TableCell>
                  <TableCell>Observação</TableCell>
                  {podeLancarVolume && <TableCell align="right">Ações</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {volumes.map((v) => {
                  const subiu = (v.variacaoAbsoluta ?? 0) > 0
                  const caiu = (v.variacaoAbsoluta ?? 0) < 0
                  return (
                    <TableRow key={v.id} hover>
                      <TableCell>{rotuloCompetencia(v.competencia)}</TableCell>
                      <TableCell align="right">{formatCurrency(v.volume)}</TableCell>
                      <TableCell align="right">
                        {v.variacaoAbsoluta == null ? (
                          // Primeiro mês do histórico: não há contra o que comparar.
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        ) : (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            {subiu ? (
                              <TrendingUpIcon fontSize="small" color="success" />
                            ) : caiu ? (
                              <TrendingDownIcon fontSize="small" color="error" />
                            ) : (
                              <TrendingFlatIcon fontSize="small" color="disabled" />
                            )}
                            <Typography
                              variant="body2"
                              color={subiu ? 'success.main' : caiu ? 'error.main' : 'text.secondary'}
                            >
                              {formatCurrency(v.variacaoAbsoluta)}
                              {/* Sem percentual quando o mês anterior foi zero. */}
                              {v.variacaoPercentual != null && ` (${v.variacaoPercentual.toFixed(2)}%)`}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {v.observacao || '—'}
                        </Typography>
                      </TableCell>
                      {podeLancarVolume && (
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => removerVolume(v.id)}
                          >
                            Remover
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Registrar volume do mês */}
      <Dialog
        open={volumeDialogOpen}
        onClose={() => setVolumeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Registrar Volume do Mês</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Competência"
              type="month"
              fullWidth
              size="small"
              value={novoVolume.competencia}
              onChange={(e) => setNovoVolume({ ...novoVolume, competencia: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Relançar um mês já registrado corrige o valor."
            />
            <TextField
              label="Volume operado (R$)"
              type="number"
              fullWidth
              size="small"
              value={novoVolume.valor}
              onChange={(e) => setNovoVolume({ ...novoVolume, valor: e.target.value })}
              inputProps={{ min: 0, step: '0.01' }}
            />
            <TextField
              label="Observação"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={novoVolume.observacao}
              onChange={(e) => setNovoVolume({ ...novoVolume, observacao: e.target.value })}
              placeholder="Ex.: queda por sazonalidade"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVolumeDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={salvarVolume}
            disabled={salvandoVolume || !novoVolume.competencia || novoVolume.valor === ''}
          >
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Contato Dialog */}
      <Dialog
        open={contatoDialogOpen}
        onClose={() => setContatoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adicionar Contato</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Nome"
              fullWidth
              value={newContato.nome}
              onChange={(e) => setNewContato({ ...newContato, nome: e.target.value })}
              error={!!contatoErrors.nome}
              helperText={contatoErrors.nome}
              size="small"
            />
            <TextField
              label="Email"
              fullWidth
              value={newContato.email}
              onChange={(e) => setNewContato({ ...newContato, email: e.target.value })}
              error={!!contatoErrors.email}
              helperText={contatoErrors.email}
              size="small"
            />
            <TextField
              label="Telefone"
              fullWidth
              value={newContato.telefone}
              onChange={(e) => setNewContato({ ...newContato, telefone: e.target.value })}
              error={!!contatoErrors.telefone}
              helperText={contatoErrors.telefone}
              size="small"
            />
            <TextField
              label="Cargo"
              fullWidth
              value={newContato.cargo}
              onChange={(e) => setNewContato({ ...newContato, cargo: e.target.value })}
              error={!!contatoErrors.cargo}
              helperText={contatoErrors.cargo}
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContatoDialogOpen(false)} disabled={salvandoContato}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleAddContato} disabled={salvandoContato}>
            {salvandoContato ? 'Salvando…' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ClienteDetailPage
